import { PackageSalesPortalConfiguration, SalesPortalAthleteRetrieve } from 'types';

import { WhoIsThisFormOption } from '../../CheckoutContext';

export type AthleteInformationField =
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'dob'
  | 'gender'
  | 'address'
  | 'emergencyName'
  | 'emergencyPhone'
  | 'medicalNotes';

const hasValue = (value: string | null | undefined): boolean => !!value && value.trim().length > 0;

// Returning buyer (spec Item 9): the read-only "On file" treatment applies only to the
// "Myself" path of a buyer with a completed prior purchase. Linked / dependent targets
// always get the standard entry fields — it is a different person's data.
export function isReturningBuyerOnFileMode(
  whoIsThisFor: WhoIsThisFormOption,
  athlete: SalesPortalAthleteRetrieve | undefined,
): boolean {
  return whoIsThisFor === 'myself' && !!athlete?.hasPriorPurchase;
}

// The visible fields the profile already has a value for. Everything on file locks;
// genuinely missing fields stay editable (and are saved back to the profile by the
// checkout POST). Hidden fields are ignored so the summary mirrors the package config.
export function computeOnFileFields(
  athlete: SalesPortalAthleteRetrieve,
  pkgConfig: PackageSalesPortalConfiguration,
): Set<AthleteInformationField> {
  const address = athlete.address;
  const addressOnFile =
    !!address && hasValue(address.addressLines) && hasValue(address.locality) && hasValue(address.zipCode);

  const candidates: Array<[AthleteInformationField, boolean, boolean]> = [
    ['firstName', true, hasValue(athlete.firstName)],
    ['lastName', true, hasValue(athlete.lastName)],
    ['phone', true, hasValue(athlete.phone)],
    ['dob', pkgConfig.dobVisible, hasValue(athlete.dob)],
    ['gender', pkgConfig.genderVisible, hasValue(athlete.gender)],
    ['address', pkgConfig.addressVisible, addressOnFile],
    ['emergencyName', pkgConfig.emergencyNameVisible, hasValue(athlete.emergencyName)],
    ['emergencyPhone', pkgConfig.emergencyPhoneVisible, hasValue(athlete.emergencyPhone)],
    ['medicalNotes', pkgConfig.notesVisible, hasValue(athlete.medicalNotes)],
  ];

  return new Set(candidates.filter(([, visible, onFile]) => visible && onFile).map(([field]) => field));
}

// The fields the package shows for this checkout, in display order.
export function visibleAthleteInformationFields(
  pkgConfig: PackageSalesPortalConfiguration,
): AthleteInformationField[] {
  const fields: AthleteInformationField[] = ['firstName', 'lastName', 'phone'];
  if (pkgConfig.dobVisible) fields.push('dob');
  if (pkgConfig.genderVisible) fields.push('gender');
  if (pkgConfig.addressVisible) fields.push('address');
  if (pkgConfig.emergencyNameVisible) fields.push('emergencyName');
  if (pkgConfig.emergencyPhoneVisible) fields.push('emergencyPhone');
  if (pkgConfig.notesVisible) fields.push('medicalNotes');
  return fields;
}
